import { useEffect, useState } from "react";
import api, { apiError, fmtMoney, fmtDate, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, ClipboardCheck, ArrowDownUp, DoorOpen, Printer, Receipt, AlertTriangle, FileSpreadsheet, FileDown } from "lucide-react";
import { toast } from "sonner";
import { exportXlsx, exportPdf } from "@/lib/exporters";

export default function CashClosure({ slug }) {
  const [accounts, setAccounts] = useState([]);
  const [closures, setClosures] = useState([]);
  const [openings, setOpenings] = useState([]);
  const [open, setOpen] = useState(false);
  const [openingOpen, setOpeningOpen] = useState(false);
  const [acc, setAcc] = useState(null);
  const [preview, setPreview] = useState(null);
  const [counted, setCounted] = useState("");
  const [notes, setNotes] = useState("");
  const [openingAmount, setOpeningAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [xreport, setXreport] = useState(null);

  const load = () => {
    api.get(`/store/${slug}/treasury-accounts`).then((r) => setAccounts(r.data)).catch(() => {});
    api.get(`/store/${slug}/treasury/closures`).then((r) => setClosures(r.data)).catch(() => {});
    api.get(`/store/${slug}/treasury/openings`).then((r) => setOpenings(r.data)).catch(() => {});
  };

  const num = (n) => Math.round(Number(n) || 0);
  const stamp = new Date().toLocaleString("fr-FR");

  const OPENING_COLS = ["Caisse", "Fond d'ouverture", "Opérateur", "Date & heure"];
  const CLOSURE_COLS = ["Caisse", "Ouverture", "Encaissé", "Décaissé", "Attendu", "Compté", "Écart", "Opérateur", "Date & heure"];

  const exportOpenings = (kind) => {
    if (!openings.length) return toast.info("Aucune ouverture à exporter");
    if (kind === "xlsx") {
      exportXlsx("journal-ouvertures-caisse.xlsx", "Ouvertures", OPENING_COLS,
        openings.map((o) => [o.account_name, num(o.opening_amount), o.user, fmtDateTime(o.created_at)]));
    } else {
      exportPdf("journal-ouvertures-caisse.pdf", "Journal des ouvertures de caisse", `Vision POS · export du ${stamp}`, OPENING_COLS,
        openings.map((o) => [o.account_name, fmtMoney(o.opening_amount), o.user, fmtDateTime(o.created_at)]));
    }
  };

  const exportClosures = (kind) => {
    if (!closures.length) return toast.info("Aucune clôture à exporter");
    if (kind === "xlsx") {
      exportXlsx("journal-clotures-z.xlsx", "Clotures Z", CLOSURE_COLS,
        closures.map((c) => [c.account_name, c.opening != null ? num(c.opening) : "", num(c.total_in), num(c.total_out), num(c.expected), num(c.counted), num(c.ecart), c.user, fmtDateTime(c.created_at)]));
    } else {
      exportPdf("journal-clotures-z.pdf", "Journal des clôtures de caisse (Z)", `Vision POS · export du ${stamp}`, CLOSURE_COLS,
        closures.map((c) => [c.account_name, c.opening != null ? fmtMoney(c.opening) : "—", fmtMoney(c.total_in), fmtMoney(c.total_out), fmtMoney(c.expected), fmtMoney(c.counted), (c.ecart > 0 ? "+" : "") + fmtMoney(c.ecart), c.user, fmtDateTime(c.created_at)]));
    }
  };

  const ExportBtns = ({ onExport, prefix }) => (
    <div className="flex gap-2">
      <Button size="sm" variant="outline" onClick={() => onExport("xlsx")} className="border-white/15 text-slate-200 bg-transparent hover:bg-white/5 h-8" data-testid={`${prefix}-export-xlsx`}><FileSpreadsheet size={13} className="mr-1" /> Excel</Button>
      <Button size="sm" variant="outline" onClick={() => onExport("pdf")} className="border-white/15 text-slate-200 bg-transparent hover:bg-white/5 h-8" data-testid={`${prefix}-export-pdf`}><FileDown size={13} className="mr-1" /> PDF</Button>
    </div>
  );
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const startOpen = (a) => { setAcc(a); setOpeningAmount(String(a.balance || "")); setOpeningOpen(true); };
  const doOpen = async () => {
    setSaving(true);
    try { await api.post(`/store/${slug}/treasury/open`, { account_id: acc.id, opening_amount: Number(openingAmount || 0) }); toast.success("Fond de caisse enregistré"); setOpeningOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const startClose = async (a) => {
    setAcc(a); setCounted(""); setNotes(""); setPreview(null); setOpen(true);
    try { const { data } = await api.get(`/store/${slug}/treasury/close-preview`, { params: { account_id: a.id } }); setPreview(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const showX = async (a) => {
    setXreport({ account_name: a.name, loading: true });
    try { const { data } = await api.get(`/store/${slug}/treasury/close-preview`, { params: { account_id: a.id } }); setXreport({ account_name: a.name, ...data }); }
    catch (e) { toast.error(apiError(e)); setXreport(null); }
  };

  const ecart = preview ? Math.round((Number(counted || 0) - preview.expected) * 100) / 100 : 0;

  const confirm = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/store/${slug}/treasury/close`, { account_id: acc.id, counted: Number(counted || 0), notes });
      toast.success("Caisse clôturée"); setOpen(false); setTicket(data); load();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  return (
    <div className="mb-8">
      <div className="vp-card rounded-xl p-6">
        <h2 className="font-display text-2xl font-bold text-white mb-1 flex items-center gap-2"><ClipboardCheck size={20} /> Ouverture & Clôture de caisse (Z)</h2>
        <p className="text-sm text-slate-400 mb-4">Saisissez le fond d'ouverture en début de journée, puis comptez le solde réel à la clôture — l'écart est calculé automatiquement.</p>
        {accounts.some((a) => a.needs_closure) && (
          <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-4" data-testid="closure-reminder">
            <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={16} />
            <p className="text-sm text-slate-300">Pensez à clôturer en fin de journée : <b className="text-white">{accounts.filter((a) => a.needs_closure).map((a) => a.name).join(", ")}</b></p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {accounts.map((a) => (
            <div key={a.id} className="bg-black/20 rounded-lg p-4">
              <div className="text-sm text-white">{a.name}</div>
              <div className="mono vp-accent-text mb-3">{fmtMoney(a.balance)}</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => startOpen(a)} className="border-white/15 text-slate-200 bg-transparent hover:bg-white/5 flex-1" data-testid="cash-open-btn"><DoorOpen size={14} className="mr-1" /> Ouvrir</Button>
                <Button size="sm" onClick={() => startClose(a)} className="vp-accent-bg text-black font-medium flex-1" data-testid="cash-close-btn"><ArrowDownUp size={14} className="mr-1" /> Clôturer</Button>
              </div>
              <Button size="sm" variant="ghost" onClick={() => showX(a)} className="text-slate-400 hover:text-white w-full mt-2 h-7" data-testid="cash-xreport-btn"><Receipt size={13} className="mr-1" /> Rapport X (sans clôture)</Button>
            </div>
          ))}
          {accounts.length === 0 && <p className="text-sm text-slate-500">Aucune caisse. Créez-en une ci-dessous.</p>}
        </div>

        {closures.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-300">Historique des clôtures</h3>
              <ExportBtns onExport={exportClosures} prefix="closures" />
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
                  {["Caisse", "Ouverture", "Attendu", "Compté", "Écart", "Par", "Date", ""].map((h) => <TableHead key={h} className="text-slate-400 text-xs uppercase tracking-wider">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {closures.map((c) => (
                    <TableRow key={c.id} className="border-white/5" data-testid="closure-row">
                      <TableCell className="text-white text-sm">{c.account_name}</TableCell>
                      <TableCell className="mono text-sm text-slate-400">{c.opening != null ? fmtMoney(c.opening) : "—"}</TableCell>
                      <TableCell className="mono text-sm text-slate-300">{fmtMoney(c.expected)}</TableCell>
                      <TableCell className="mono text-sm text-slate-300">{fmtMoney(c.counted)}</TableCell>
                      <TableCell className={`mono text-sm ${c.ecart === 0 ? "text-emerald-400" : c.ecart > 0 ? "text-blue-400" : "text-red-400"}`}>{c.ecart > 0 ? "+" : ""}{fmtMoney(c.ecart)}</TableCell>
                      <TableCell className="text-slate-400 text-sm">{c.user}</TableCell>
                      <TableCell className="text-slate-400 text-sm">{fmtDate(c.created_at)}</TableCell>
                      <TableCell><Button size="sm" variant="ghost" onClick={() => setTicket(c)} className="text-slate-300 hover:text-white h-8" data-testid="closure-ticket-btn"><Receipt size={14} className="mr-1" /> Ticket</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
        {openings.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-300">Journal des ouvertures</h3>
              <ExportBtns onExport={exportOpenings} prefix="openings" />
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
                  {["Caisse", "Fond d'ouverture", "Opérateur", "Heure"].map((h) => <TableHead key={h} className="text-slate-400 text-xs uppercase tracking-wider">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {openings.map((o) => (
                    <TableRow key={o.id} className="border-white/5" data-testid="opening-row">
                      <TableCell className="text-white text-sm">{o.account_name}</TableCell>
                      <TableCell className="mono text-sm text-slate-300">{fmtMoney(o.opening_amount)}</TableCell>
                      <TableCell className="text-slate-400 text-sm">{o.user}</TableCell>
                      <TableCell className="text-slate-400 text-sm">{fmtDateTime(o.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* Opening dialog */}
      <Dialog open={openingOpen} onOpenChange={setOpeningOpen}>
        <DialogContent className="vp-card border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><DoorOpen size={20} /> Fond d'ouverture</DialogTitle>
            <DialogDescription className="text-slate-400">Montant en caisse au début de la journée — {acc?.name}</DialogDescription></DialogHeader>
          <div className="py-2"><Label className="text-slate-300 text-sm mb-1.5 block">Montant du fond</Label>
            <Input type="number" value={openingAmount} onChange={(e) => setOpeningAmount(e.target.value)} className="bg-black/30 border-white/10 text-white mono text-lg h-12" data-testid="cash-opening-amount" /></div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpeningOpen(false)} className="text-slate-400">Annuler</Button>
            <Button onClick={doOpen} disabled={saving} className="vp-accent-bg text-black font-medium" data-testid="cash-open-confirm">{saving && <Loader2 className="animate-spin mr-2" size={15} />} Enregistrer le fond</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="vp-card border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><ArrowDownUp size={20} /> Clôture Z — {acc?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">Comptez le montant physiquement présent et confirmez.</DialogDescription>
          </DialogHeader>
          {!preview ? <div className="py-6 text-center text-slate-400"><Loader2 className="animate-spin inline mr-2" size={16} /> Calcul…</div> : (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="bg-black/20 rounded-lg p-2"><div className="text-[10px] text-slate-500">Ouverture</div><div className="mono text-slate-200 text-xs">{preview.opening != null ? fmtMoney(preview.opening) : "—"}</div></div>
                <div className="bg-black/20 rounded-lg p-2"><div className="text-[10px] text-slate-500">Encaissé</div><div className="mono text-emerald-400 text-xs">{fmtMoney(preview.today_in)}</div></div>
                <div className="bg-black/20 rounded-lg p-2"><div className="text-[10px] text-slate-500">Décaissé</div><div className="mono text-red-400 text-xs">{fmtMoney(preview.today_out)}</div></div>
                <div className="bg-black/20 rounded-lg p-2"><div className="text-[10px] text-slate-500">Attendu</div><div className="mono text-white text-xs">{fmtMoney(preview.expected)}</div></div>
              </div>
              {preview.by_method?.length > 0 && (
                <div className="bg-black/20 rounded-lg p-3">
                  <div className="text-xs text-slate-500 mb-1">Encaissements par mode</div>
                  {preview.by_method.map((m) => <div key={m.method} className="flex justify-between text-xs text-slate-300"><span>{m.method} <span className="text-slate-500">×{m.count}</span></span><span className="mono">{fmtMoney(m.amount)}</span></div>)}
                </div>
              )}
              {preview.by_seller?.length > 0 && (
                <div className="bg-black/20 rounded-lg p-3">
                  <div className="text-xs text-slate-500 mb-1">Ventes par vendeur (aujourd'hui)</div>
                  {preview.by_seller.map((s) => <div key={s.seller} className="flex justify-between text-xs text-slate-300"><span>{s.seller} <span className="text-slate-500">×{s.count}</span></span><span className="mono">{fmtMoney(s.amount)}</span></div>)}
                </div>
              )}
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Montant compté</Label>
                <Input type="number" value={counted} onChange={(e) => setCounted(e.target.value)} className="bg-black/30 border-white/10 text-white mono text-lg h-12" data-testid="cash-counted" placeholder="0" /></div>
              <div className={`flex items-center justify-between rounded-lg p-3 ${ecart === 0 ? "bg-emerald-500/10" : ecart > 0 ? "bg-blue-500/10" : "bg-red-500/10"}`}>
                <span className="text-sm text-slate-300">Écart de caisse</span>
                <span className={`mono text-lg font-bold ${ecart === 0 ? "text-emerald-400" : ecart > 0 ? "text-blue-400" : "text-red-400"}`} data-testid="cash-ecart">{ecart > 0 ? "+" : ""}{fmtMoney(ecart)}</span>
              </div>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-black/30 border-white/10 text-white" placeholder="Notes (optionnel)" />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">Annuler</Button>
            <Button onClick={confirm} disabled={saving || !preview || counted === ""} className="vp-accent-bg text-black font-medium" data-testid="cash-close-confirm">{saving && <Loader2 className="animate-spin mr-2" size={15} />} Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Z ticket (printable) */}
      <Dialog open={!!xreport} onOpenChange={() => setXreport(null)}>
        <DialogContent className="vp-card border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl">Rapport X</DialogTitle>
            <DialogDescription className="text-slate-400">Situation en cours (sans clôturer) — {xreport?.account_name}</DialogDescription></DialogHeader>
          {xreport?.loading ? <div className="py-6 text-center text-slate-400"><Loader2 className="animate-spin inline mr-2" size={16} /> Calcul…</div> : xreport && (
            <div className="space-y-1.5 py-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Fond d'ouverture</span><span className="mono">{xreport.opening != null ? fmtMoney(xreport.opening) : "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Encaissements</span><span className="mono text-emerald-400">{fmtMoney(xreport.today_in)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Décaissements</span><span className="mono text-red-400">{fmtMoney(xreport.today_out)}</span></div>
              {xreport.by_method?.length > 0 && (
                <div className="border-t border-white/10 pt-1.5 mt-1.5">
                  <div className="text-xs text-slate-500 mb-1">Par mode</div>
                  {xreport.by_method.map((m) => <div key={m.method} className="flex justify-between text-xs"><span className="text-slate-400">{m.method} ×{m.count}</span><span className="mono">{fmtMoney(m.amount)}</span></div>)}
                </div>
              )}
              {xreport.by_seller?.length > 0 && (
                <div className="border-t border-white/10 pt-1.5 mt-1.5">
                  <div className="text-xs text-slate-500 mb-1">Par vendeur</div>
                  {xreport.by_seller.map((s) => <div key={s.seller} className="flex justify-between text-xs"><span className="text-slate-400">{s.seller} ×{s.count}</span><span className="mono">{fmtMoney(s.amount)}</span></div>)}
                </div>
              )}
              <div className="flex justify-between border-t border-white/10 pt-1.5 mt-1.5 font-bold"><span>Solde théorique</span><span className="mono vp-accent-text">{fmtMoney(xreport.expected)}</span></div>
            </div>
          )}
          <DialogFooter><Button onClick={() => setXreport(null)} className="vp-accent-bg text-black font-medium">Fermer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Z ticket (printable) */}
      <Dialog open={!!ticket} onOpenChange={() => setTicket(null)}>
        <DialogContent className="vp-card border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl">Ticket de clôture Z</DialogTitle>
            <DialogDescription className="text-slate-400">Rapport de caisse — {ticket?.account_name}</DialogDescription></DialogHeader>
          {ticket && (
            <div className="z-print space-y-1.5 py-2 text-sm">
              <div className="text-center border-b border-white/10 pb-2 mb-2">
                <div className="font-display text-lg font-bold">{ticket.store_name || "Vision POS"}</div>
                <div className="text-xs text-slate-400">Clôture Z · {ticket.account_name}</div>
                <div className="text-xs text-slate-500">{fmtDate(ticket.created_at)} · {ticket.user}</div>
              </div>
              <div className="flex justify-between"><span className="text-slate-400">Fond d'ouverture</span><span className="mono">{ticket.opening != null ? fmtMoney(ticket.opening) : "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Encaissements</span><span className="mono text-emerald-500">{fmtMoney(ticket.total_in)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Décaissements</span><span className="mono text-red-500">{fmtMoney(ticket.total_out)}</span></div>
              {ticket.by_method?.length > 0 && (
                <div className="border-t border-white/10 pt-1.5 mt-1.5">
                  <div className="text-xs text-slate-500 mb-1">Détail encaissements par mode</div>
                  {ticket.by_method.map((m) => <div key={m.method} className="flex justify-between text-xs"><span className="text-slate-400">{m.method} ×{m.count}</span><span className="mono">{fmtMoney(m.amount)}</span></div>)}
                </div>
              )}
              {ticket.by_seller?.length > 0 && (
                <div className="border-t border-white/10 pt-1.5 mt-1.5">
                  <div className="text-xs text-slate-500 mb-1">Ventes par vendeur</div>
                  {ticket.by_seller.map((s) => <div key={s.seller} className="flex justify-between text-xs"><span className="text-slate-400">{s.seller} ×{s.count}</span><span className="mono">{fmtMoney(s.amount)}</span></div>)}
                </div>
              )}
              <div className="flex justify-between border-t border-white/10 pt-1.5 mt-1.5"><span className="text-slate-400">Solde attendu</span><span className="mono">{fmtMoney(ticket.expected)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Solde compté</span><span className="mono">{fmtMoney(ticket.counted)}</span></div>
              <div className="flex justify-between font-bold text-base"><span>Écart</span><span className="mono">{ticket.ecart > 0 ? "+" : ""}{fmtMoney(ticket.ecart)}</span></div>
              {ticket.notes && <div className="text-xs text-slate-500 pt-2">Note: {ticket.notes}</div>}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTicket(null)} className="text-slate-400">Fermer</Button>
            <Button onClick={() => window.print()} className="vp-accent-bg text-black font-medium" data-testid="ticket-print-btn"><Printer size={15} className="mr-1" /> Imprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
